import React, { useState, useEffect } from 'react';
import axios from './axiosConfig';
import { motion, AnimatePresence } from 'framer-motion';
import '../App.css';

// Variantes de animación
const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

function AIAnalysis() {
  const [publicaciones, setPublicaciones] = useState([]);
  const [publicacionId, setPublicacionId] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;
  const [showKeywordsModal, setShowKeywordsModal] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState([]);

  useEffect(() => {
    fetchPublicaciones();
  }, []);

  const fetchPublicaciones = async () => {
    try {
      const response = await axios.get('/api/publicaciones/');
      if (Array.isArray(response.data)) {
        setPublicaciones(response.data);
        setError(null);
      } else {
        setPublicaciones([]);
        setError('La respuesta del servidor no es una lista válida');
      }
    } catch (error) {
      setPublicaciones([]);
      setError('Error al obtener publicaciones: ' + error.message);
      console.error('Error fetching publicaciones:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAnalysis(null);
    try {
      const response = await axios.get(`/api/ai/analyze/${publicacionId}`);
      setAnalysis(response.data);
      setError(null);
    } catch (error) {
      setError('Error al analizar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <motion.header 
        className="bg-surface-light dark:bg-surface-dark shadow-sm border-b border-border-light dark:border-border-dark z-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="max-w-1xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h2 className="text-xl font-bold leading-7 text-slate-900 dark:text-white sm:truncate">Análisis de Publicaciones con IA</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Utiliza inteligencia artificial para analizar y clasificar publicaciones científicas</p>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div 
              className="bg-red-50 border border-red-200 rounded-md p-4 mb-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <div className="flex">
                <span className="material-icons-round text-red-400 mr-3">error</span>
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="text-sm text-red-700 mt-1">{error}</div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Selector de Publicación */}
        <motion.section 
          className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-sm border border-border-light dark:border-border-dark overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="p-5 sm:p-6">
            <div className="flex items-center mb-4">
              <motion.span 
                className="material-icons-round text-primary mr-2"
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                psychology
              </motion.span>
              <h3 className="text-lg font-medium text-slate-900 dark:text-white">Seleccionar Publicación para Análisis</h3>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Publicación</label>
                <select
                  className="block w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-md shadow-sm bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  value={publicacionId}
                  onChange={(e) => setPublicacionId(e.target.value)}
                  required
                >
                  <option value="">-- Seleccione una publicación --</option>
                  {publicaciones.map((pub) => (
                    <option key={pub.id} value={pub.id}>{pub.titulo}</option>
                  ))}
                </select>
              </motion.div>
              <motion.button 
                type="submit" 
                disabled={!publicacionId || loading}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-secondary hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-md transition-all disabled:opacity-50"
                whileHover={{ scale: loading ? 1 : 1.05 }}
                whileTap={{ scale: loading ? 1 : 0.95 }}
              >
                {loading ? (
                  <>
                    <motion.span 
                      className="material-icons-round mr-2"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      refresh
                    </motion.span>
                    Analizando...
                  </>
                ) : (
                  <>
                    <span className="material-icons-round mr-2">search</span>
                    Analizar con IA
                  </>
                )}
              </motion.button>
            </form>
            
            {/* Loading indicator */}
            <AnimatePresence>
              {loading && (
                <motion.div 
                  className="text-center py-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.span 
                    className="material-icons-round text-primary text-3xl"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    refresh
                  </motion.span>
                  <motion.p 
                    className="text-slate-500 dark:text-slate-400 mt-2"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    Analizando publicación...
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.section>

        {/* Resultados del Análisis */}
        <AnimatePresence>
          {analysis && (
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <div className="flex items-center justify-between mb-4 px-1">
                <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Resultados del Análisis IA</h3>
                <motion.span 
                  className="text-xs text-slate-400 dark:text-slate-500"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  Procesamiento completado
                </motion.span>
              </div>
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-3 gap-5"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                <motion.div 
                  className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
                  variants={scaleIn}
                  whileHover={{ scale: 1.03, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-round text-6xl text-primary">category</span>
                  </div>
                  <div className="flex items-center mb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      Área de Conocimiento
                    </span>
                  </div>
                  <h4 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">{analysis.area_conocimiento}</h4>
                  <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700/50 pt-3">
                    <span className="text-xs font-medium text-primary">Clasificación automática</span>
                  </div>
                </motion.div>
                <motion.div 
                  className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
                  variants={scaleIn}
                  whileHover={{ scale: 1.03, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-round text-6xl text-green-500">label</span>
                  </div>
                  <div className="flex items-center mb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                      Palabras Clave
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {analysis.palabras_clave.slice(0, 3).map((kw, index) => (
                      <motion.span 
                        key={index} 
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500 text-white"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        {kw}
                      </motion.span>
                    ))}
                    {analysis.palabras_clave.length > 3 && (
                      <button
                        onClick={() => {
                          setSelectedKeywords(analysis.palabras_clave);
                          setShowKeywordsModal(true);
                        }}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500 text-white hover:bg-green-600 transition-colors cursor-pointer"
                      >
                        +{analysis.palabras_clave.length - 3}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700/50 pt-3">
                    <span className="text-xs font-medium text-green-600">Extracción semántica</span>
                  </div>
                </motion.div>
                <motion.div 
                  className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group"
                  variants={scaleIn}
                  whileHover={{ scale: 1.03, y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="material-icons-round text-6xl text-yellow-500">public</span>
                  </div>
                  <div className="flex items-center mb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                      Alineación ODS
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {analysis.ods.slice(0, 2).map((ods, index) => (
                      <motion.span 
                        key={index} 
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500 text-white"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        {ods}
                      </motion.span>
                    ))}
                    {analysis.ods.length > 2 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500 text-white">
                        +{analysis.ods.length - 2}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700/50 pt-3">
                    <span className="text-xs font-medium text-yellow-600">Impacto social</span>
                  </div>
                </motion.div>
              </motion.div>
            
            {/* Resumen Completo */}
            <div className="mt-6 bg-surface-light dark:bg-surface-dark rounded-xl shadow-sm border border-border-light dark:border-border-dark overflow-hidden">
              <div className="p-6">
                <h4 className="text-base font-medium text-slate-900 dark:text-white mb-3 flex items-center">
                  <span className="material-icons-round mr-2">summarize</span>
                  Resumen Completo
                </h4>
                <div className="bg-slate-50 dark:bg-slate-700 p-6 rounded-lg border border-slate-200 dark:border-slate-600">
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">{analysis.resumen}</p>
                </div>
              </div>
            </div>
          </motion.section>
        )}
        </AnimatePresence>

        {/* Lista de Publicaciones */}
        <section className="bg-surface-light dark:bg-surface-dark shadow-md rounded-xl border border-border-light dark:border-border-dark overflow-hidden">
          <div className="px-6 py-4 border-b border-border-light dark:border-border-dark flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center">
              <span className="material-icons-round mr-2 text-slate-500">library_books</span>
              Lista de Publicaciones
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300">
              {publicaciones.length} registros
            </span>
          </div>
          
          {publicaciones.length === 0 ? (
            <div className="text-center py-12">
              <span className="material-icons-round text-slate-400 text-6xl mb-4">library_books</span>
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No hay publicaciones disponibles</h3>
              <p className="text-slate-500 dark:text-slate-400">Agrega publicaciones para comenzar el análisis</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border-light dark:divide-border-dark">
                <thead className="bg-slate-100 dark:bg-slate-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Título</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Abstract</th>
                  </tr>
                </thead>
                <tbody className="bg-surface-light dark:bg-surface-dark divide-y divide-border-light dark:divide-border-dark">
                  {publicaciones.slice(currentPage * pageSize, (currentPage + 1) * pageSize).map((pub) => (
                    <tr key={pub.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400 font-mono">{pub.id}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">{pub.titulo}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-600 dark:text-slate-400 max-w-md truncate">
                          {pub.abstract ? (
                            pub.abstract
                          ) : (
                            <span className="text-slate-400 italic">Sin abstract</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        
        {/* Pagination for Publications Table */}
        {publicaciones.length > pageSize && (
          <div className="mt-6 flex justify-center">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                <span className="material-icons-round text-base">chevron_left</span>
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-300">
                Página {currentPage + 1} de {Math.ceil(publicaciones.length / pageSize)}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(Math.floor((publicaciones.length - 1) / pageSize), p + 1))}
                disabled={(currentPage + 1) * pageSize >= publicaciones.length}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                <span className="material-icons-round text-base">chevron_right</span>
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Modal de Keywords */}
      <AnimatePresence>
        {showKeywordsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            >
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    Palabras Clave Completas
                  </h3>
                  <button
                    onClick={() => setShowKeywordsModal(false)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    <span className="material-icons-round">close</span>
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="flex flex-wrap gap-2">
                  {selectedKeywords.map((kw, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-green-500 text-white"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </>
  );
}

export default AIAnalysis;
